import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js";
import TableIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Table.js";
import ColumnsIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Columns.js";
import SearchInput from "./SearchInput";
import FilterMenu from "./FilterMenu";
import {
  policyApiService,
  type LanguageOption,
} from "../services/policyApiService";
import { languagesCacheService } from "../services/languagesCacheService";

interface SearchAndFilterAreaProps {
  // Search props
  searchValue: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement> | string) => void;
  onSearchKeyPress?: (event: React.KeyboardEvent) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;

  // Filter and action props
  showGridView?: boolean;
  showSortBy?: boolean;
  showLanguage?: boolean;
  showFilterButton?: boolean;

  viewMode?: "table" | "grid";

  // Event handlers
  onGridView?: () => void;
  onSortSelection?: (sortOption: string) => void;
  onLanguageSelection?: (languageOption: string) => void;
  onOpenFilter?: () => void;
}

export const SearchAndFilterArea: React.FC<SearchAndFilterAreaProps> = ({
  searchValue,
  onSearchChange,
  onSearchKeyPress,
  searchPlaceholder = "Ask a question about...",
  showSearch = true,
  showGridView = false,
  showSortBy = true,
  showLanguage = true,
  showFilterButton = false,
  viewMode = "table",
  onGridView,
  onSortSelection,
  onLanguageSelection,
  onOpenFilter,
}) => {
  const theme = useTheme();
  // Use atlas theme breakpoints: sm: 320px, md: 481px, lg: 769px, xl: 1280px
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md")); // below 481px (3-row structure)
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg")); // below 769px (grid icon only)  // lg and up = default when others are false

  // Sort menu state
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortMenuOpen = Boolean(sortAnchorEl);

  // Language menu state
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const languageMenuOpen = Boolean(languageAnchorEl);

  // Dynamic language options state
  const [availableLanguages, setAvailableLanguages] = useState<
    LanguageOption[]
  >([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);

  // Lazy load available languages from API (only when dropdown is opened)
  const loadLanguages = async (forceRefresh = false) => {
    if (languagesLoaded || languagesLoading) return; // Don't load if already loaded/loading

    try {
      // Try to get from cache (unless force refresh is requested)
      if (!forceRefresh) {
        const cachedLanguages = languagesCacheService.getCachedLanguages();
        if (cachedLanguages) {
          setAvailableLanguages(cachedLanguages);
          setLanguagesLoaded(true);
          return;
        }
      } else {
        // Force refresh requested, clear cache
        languagesCacheService.clearCache();
      }

      // Cache miss or expired, fetch from API
      setLanguagesLoading(true);
      const response = await policyApiService.getAvailableLanguages();

      // Cache the response
      languagesCacheService.setCachedLanguages(response.data);

      setAvailableLanguages(response.data);
      setLanguagesLoaded(true);
    } catch (error) {
      console.error("Failed to load available languages:", error);
      // Fallback to empty array, "All" option will still be available
      setAvailableLanguages([]);
      setLanguagesLoaded(true); // Mark as loaded even on error to prevent retries
    } finally {
      setLanguagesLoading(false);
    }
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (sortOption: string) => {
    onSortSelection?.(sortOption);
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLanguageAnchorEl(event.currentTarget);
    // Lazy load languages when dropdown is opened
    loadLanguages();
  };

  const handleLanguageClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageSelect = (languageOption: string) => {
    // Don't select the loading item
    if (languageOption === "loading") return;

    onLanguageSelection?.(languageOption);
  };

  // Sort menu items
  const sortMenuItems = [
    { id: "all-documents", label: "All documents" },
    { id: "recently-updated", label: "Recently updated" },
    { id: "recently-added", label: "Recently added" },
  ];

  // Language menu items - dynamic based on API data with proper display names
  const languageMenuItems = [
    { id: "all", label: "All" },
    ...(languagesLoading
      ? [{ id: "loading", label: "Loading..." }]
      : availableLanguages.map((lang) => ({
          id: lang.code, // Use the code as the ID for the request
          label: lang.display, // Use the display name from the API
        }))),
  ];

  // Get icon and text based on current view mode
  const getViewButtonProps = () => {
    if (viewMode === "grid") {
      return {
        icon: <ColumnsIcon />,
        text: "Table view",
      };
    } else {
      return {
        icon: <TableIcon />,
        text: "Grid view",
      };
    }
  };

  const viewButtonProps = getViewButtonProps();

  // Below MD (below 481px): 3-row layout
  if (isBelowMd) {
    return (
      <Box sx={{ width: "100%" }}>
        {showSearch && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: theme.palette.text.primary,
              }}
            >
              Search
            </Typography>
            <SearchInput
              fullWidth
              value={searchValue}
              onChange={onSearchChange}
              onKeyPress={onSearchKeyPress}
              placeholder={searchPlaceholder}
            />
          </Box>
        )}

        {/* Row 2: Grid icon + Sort by + Filter (search page) */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {showFilterButton && (
            <Button
              variant="text"
              endIcon={<FilterIcon />}
              onClick={() => onOpenFilter?.()}
            >
              Filter
            </Button>
          )}
          {showGridView && (
            <Button
              variant="text"
              onClick={onGridView}
              sx={{ minWidth: "auto", px: 1 }}
            >
              {viewButtonProps.icon}
            </Button>
          )}

          {showSortBy && (
            <Button
              variant="text"
              endIcon={<FilterIcon />}
              onClick={handleSortClick}
            >
              Sort by
            </Button>
          )}
        </Stack>

        {/* Row 3: Language */}
        {showLanguage && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="text"
              endIcon={<FilterIcon />}
              onClick={handleLanguageClick}
            >
              Language
            </Button>
          </Box>
        )}

        {/* Filter Menus for Small Screens */}
        <FilterMenu
          anchorEl={sortAnchorEl}
          open={sortMenuOpen}
          onClose={handleSortClose}
          onSelect={handleSortSelect}
          menuItems={sortMenuItems}
          headerText="Sort by"
        />
        <FilterMenu
          anchorEl={languageAnchorEl}
          open={languageMenuOpen}
          onClose={handleLanguageClose}
          onSelect={handleLanguageSelect}
          menuItems={languageMenuItems}
          headerText="Language"
        />
      </Box>
    );
  }

  // MD to LG Screens (481px-768px): Single row layout with icon only
  if (isBelowLg && !isBelowMd) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-end"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        {showSearch && (
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: theme.palette.text.primary,
              }}
            >
              Search
            </Typography>
            <SearchInput
              fullWidth
              value={searchValue}
              onChange={onSearchChange}
              onKeyPress={onSearchKeyPress}
              placeholder="Ask a questio..."
            />
          </Box>
        )}

        <Stack direction="row" spacing={1}>
          {showFilterButton && (
            <Button
              variant="text"
              endIcon={<FilterIcon />}
              onClick={() => onOpenFilter?.()}
            >
              Filter
            </Button>
          )}
          {showGridView && (
            <Button
              variant="text"
              onClick={onGridView}
              sx={{ minWidth: "auto", px: 1 }}
            >
              {viewButtonProps.icon}
            </Button>
          )}

          {showSortBy && (
            <Button
              variant="text"
              endIcon={<FilterIcon />}
              onClick={handleSortClick}
            >
              Sort by
            </Button>
          )}

          {showLanguage && (
            <Button
              variant="text"
              endIcon={<FilterIcon />}
              onClick={handleLanguageClick}
            >
              Language
            </Button>
          )}
        </Stack>
        <FilterMenu
          anchorEl={sortAnchorEl}
          open={sortMenuOpen}
          onClose={handleSortClose}
          onSelect={handleSortSelect}
          menuItems={sortMenuItems}
          headerText="Sort by"
        />
        <FilterMenu
          anchorEl={languageAnchorEl}
          open={languageMenuOpen}
          onClose={handleLanguageClose}
          onSelect={handleLanguageSelect}
          menuItems={languageMenuItems}
          headerText="Language"
        />
      </Stack>
    );
  }

  // LG+ Screens (769px+): Single row layout with icon and "Grid view" text
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-end"
      justifyContent="space-between"
      sx={{ mb: 3 }}
    >
      {showSearch && (
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            Search
          </Typography>
          <SearchInput
            fullWidth
            value={searchValue}
            onChange={onSearchChange}
            onKeyPress={onSearchKeyPress}
            placeholder={searchPlaceholder}
          />
        </Box>
      )}

      <Stack direction="row" spacing={2}>
        {showGridView && (
          <Button
            variant="text"
            startIcon={viewButtonProps.icon}
            onClick={onGridView}
          >
            {viewButtonProps.text}
          </Button>
        )}

        {showSortBy && (
          <Button
            variant="text"
            endIcon={<FilterIcon />}
            onClick={handleSortClick}
          >
            Sort by
          </Button>
        )}

        {showLanguage && (
          <Button
            variant="text"
            endIcon={<FilterIcon />}
            onClick={handleLanguageClick}
            disabled={languagesLoading}
          >
            {languagesLoading ? "Loading..." : "Language"}
          </Button>
        )}
      </Stack>
      <FilterMenu
        anchorEl={sortAnchorEl}
        open={sortMenuOpen}
        onClose={handleSortClose}
        onSelect={handleSortSelect}
        menuItems={sortMenuItems}
        headerText="Sort by"
      />
      <FilterMenu
        anchorEl={languageAnchorEl}
        open={languageMenuOpen}
        onClose={handleLanguageClose}
        onSelect={handleLanguageSelect}
        menuItems={languageMenuItems}
        headerText="Language"
      />
    </Stack>
  );
};

export default SearchAndFilterArea;
